import React, { useState, useEffect, useCallback } from 'react';
import { Table, Card, Button, Space, Tag, Select, Input, message, Modal, Typography, Dropdown, DatePicker, Checkbox } from 'antd';
import type { MenuProps } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, MoreOutlined, ShoppingCartOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { supplierAPI, supplierQuoteAPI, quoteStatusAPI, productAPI, customerOrderAPI } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { Supplier, SupplierQuote, QuoteStatus, Product, CustomerOrder } from '../types.ts';
import { formatCurrency } from '../utils/numberFormatting.ts';
import { generateProductDisplayName } from '../utils/productUtils.ts';
import { QUOTE_STATUS, areQuoteRowsLocked, isQuoteTerminal, getQuoteStatusColor } from '../utils/quoteStatus.ts';
import SupplierQuoteForm from '../components/SupplierQuoteForm.tsx';
import SupplierQuoteDetailsModal from '../components/SupplierQuoteDetailsModal.tsx';
import ConvertSupplierQuoteModal from '../components/ConvertSupplierQuoteModal.tsx';
import QuoteStatusModal from '../components/QuoteStatusModal.tsx';

const { Option } = Select;
const { Title } = Typography;
const { RangePicker } = DatePicker;

// Preventivi Fornitori: stesso schema di Gestione Ordini Fornitori (filtri server-side,
// elenco completo, Visualizza con Cronologia, Cambia Stato con sequenza, conversione).
const SupplierQuotes: React.FC = () => {
  const { canWrite } = useAuth();
  const canEdit = canWrite('supplier-quotes');
  const [quotes, setQuotes] = useState<SupplierQuote[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [quoteStatuses, setQuoteStatuses] = useState<QuoteStatus[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [customerOrders, setCustomerOrders] = useState<CustomerOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFormModalVisible, setIsFormModalVisible] = useState(false);
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [isConvertModalVisible, setIsConvertModalVisible] = useState(false);
  const [editingQuote, setEditingQuote] = useState<SupplierQuote | null>(null);
  const [viewingQuote, setViewingQuote] = useState<SupplierQuote | null>(null);
  const [statusChangingQuote, setStatusChangingQuote] = useState<SupplierQuote | null>(null);
  const [convertingQuote, setConvertingQuote] = useState<SupplierQuote | null>(null);
  const [filters, setFilters] = useState({
    quote_number: undefined as string | undefined,
    supplier_id: undefined as number | undefined,
    status_id: undefined as number | undefined,
    customer_order_id: undefined as number | undefined,
    product_id: undefined as number | undefined,
    date_from: undefined as string | undefined,
    date_to: undefined as string | undefined,
    show_closed: false,
  });

  const loadQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await supplierQuoteAPI.getSupplierQuotes({
        quote_number: filters.quote_number,
        supplier_id: filters.supplier_id,
        status_id: filters.status_id,
        customer_order_id: filters.customer_order_id,
        product_id: filters.product_id,
        date_from: filters.date_from,
        date_to: filters.date_to,
        exclude_closed: !filters.show_closed,
        limit: 0,  // elenco completo
      });
      setQuotes(data);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : 'Errore nel caricamento dei preventivi fornitori');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadQuotes();
  }, [filters, loadQuotes]);

  const loadInitialData = async () => {
    try {
      const [suppliersData, statusesData, productsData, ordersData] = await Promise.all([
        supplierAPI.getSuppliers(),
        quoteStatusAPI.getQuoteStatuses(),
        productAPI.getProducts(),
        customerOrderAPI.getCustomerOrders({ limit: 0 }),
      ]);
      setSuppliers(suppliersData);
      setQuoteStatuses(statusesData);
      setProducts(productsData);
      setCustomerOrders(ordersData);
    } catch (error) {
      message.error('Errore nel caricamento dei dati iniziali');
    }
  };

  const errorDetail = (error: any, fallback: string) => {
    const detail = error?.response?.data?.detail;
    return typeof detail === 'string' ? detail : fallback;
  };

  const handleCreate = () => {
    setEditingQuote(null);
    setIsFormModalVisible(true);
  };

  const handleEdit = (quote: SupplierQuote) => {
    setEditingQuote(quote);
    setIsFormModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingQuote) {
        await supplierQuoteAPI.updateSupplierQuote(editingQuote.id, values);
        message.success('Preventivo fornitore aggiornato con successo');
      } else {
        await supplierQuoteAPI.createSupplierQuote(values);
        message.success('Preventivo fornitore creato con successo');
      }
      setIsFormModalVisible(false);
      loadQuotes();
    } catch (error: any) {
      message.error(errorDetail(error, 'Errore nel salvataggio del preventivo'));
    }
  };

  const handleDelete = (quote: SupplierQuote) => {
    Modal.confirm({
      title: `Eliminare il preventivo fornitore ${quote.quote_number}?`,
      content: 'Il preventivo viene eliminato e il suo numero torna riutilizzabile.',
      okText: 'Sì, elimina',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          await supplierQuoteAPI.deleteSupplierQuote(quote.id);
          message.success('Preventivo fornitore eliminato con successo');
          loadQuotes();
        } catch (error: any) {
          message.error(errorDetail(error, "Errore nell'eliminazione del preventivo"));
        }
      },
    });
  };

  const handleStatusUpdate = async (statusData: { status_id: number; notes?: string }) => {
    if (!statusChangingQuote) return;
    try {
      const response = await supplierQuoteAPI.updateQuoteStatus(statusChangingQuote.id, statusData);
      message.success(response?.message || 'Stato preventivo aggiornato');
      setIsStatusModalVisible(false);
      setStatusChangingQuote(null);
      loadQuotes();
    } catch (error: any) {
      message.error(errorDetail(error, "Errore nell'aggiornamento dello stato"));
    }
  };

  const handleDateRangeChange = (_dates: any, dateStrings: string[]) => {
    setFilters({ ...filters, date_from: dateStrings[0] || undefined, date_to: dateStrings[1] || undefined });
  };

  const getActionItems = (record: SupplierQuote): MenuProps['items'] => {
    const rowsLocked = areQuoteRowsLocked(record.quote_status_id);
    const terminal = isQuoteTerminal(record.quote_status_id);
    const converted = !!record.supplier_order;
    const items: MenuProps['items'] = [
      { key: 'view', icon: <EyeOutlined />, label: 'Visualizza', onClick: () => setViewingQuote(record) },
    ];
    if (canEdit) {
      items.push({
        key: 'edit',
        icon: <EditOutlined />,
        label: rowsLocked ? 'Modifica (solo testata)' : 'Modifica',
        disabled: terminal,
        onClick: () => handleEdit(record),
      });
      items.push({
        key: 'status',
        icon: <CheckCircleOutlined />,
        label: 'Cambia Stato',
        disabled: terminal,
        onClick: () => { setStatusChangingQuote(record); setIsStatusModalVisible(true); },
      });
      if (record.quote_status_id === QUOTE_STATUS.ACCEPTED && !converted) {
        items.push({
          key: 'convert',
          icon: <ShoppingCartOutlined />,
          label: 'Converti in Ordine Fornitore',
          onClick: () => { setConvertingQuote(record); setIsConvertModalVisible(true); },
        });
      }
      items.push({ type: 'divider' });
      items.push({
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Elimina',
        danger: true,
        disabled: converted,
        onClick: () => handleDelete(record),
      });
    }
    return items;
  };

  const renderExpiry = (record: SupplierQuote) => {
    if (isQuoteTerminal(record.quote_status_id) || !record.expiry_date) return null;
    const days = Math.ceil((new Date(record.expiry_date).getTime() - new Date().setHours(0, 0, 0, 0)) / (1000 * 3600 * 24));
    if (days < 0) return <Tag color="red" icon={<ClockCircleOutlined />}>Scaduta</Tag>;
    if (days <= 7) return <Tag color="orange" icon={<ClockCircleOutlined />}>In scadenza ({days} gg)</Tag>;
    return <Tag color="green">Valida</Tag>;
  };

  const columns = [
    { title: 'Numero Preventivo', dataIndex: 'quote_number', key: 'quote_number', render: (v: string) => <strong>{v}</strong> },
    { title: 'Fornitore', dataIndex: ['supplier', 'name'], key: 'supplier' },
    { title: 'Data', dataIndex: 'quote_date', key: 'quote_date', render: (d: string) => new Date(d).toLocaleDateString() },
    { title: 'Scadenza', key: 'expiry', render: (record: SupplierQuote) => (
      <Space size={4}>
        {record.expiry_date ? new Date(record.expiry_date).toLocaleDateString() : '-'}
        {renderExpiry(record)}
      </Space>
    ) },
    { title: 'Stato', key: 'status', render: (record: SupplierQuote) => (
      <Tag color={getQuoteStatusColor(record.quote_status)}>{record.quote_status?.display_name || 'N/A'}</Tag>
    ) },
    { title: 'Giorni Consegna', dataIndex: 'delivery_time_days', key: 'delivery_time_days', render: (days: number) => days != null ? `${days} gg` : '-' },
    { title: 'Ordine Cliente', key: 'customer_order', width: 180, render: (record: SupplierQuote) => {
      if (!record.customer_order) return '-';
      return (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.customer_order.order_number}</div>
          {record.customer_order.customer_order_number && (
            <div style={{ fontSize: '12px', color: '#666' }}>{record.customer_order.customer_order_number}</div>
          )}
          {record.customer_order.customer?.company && (
            <div style={{ fontSize: '12px', color: '#666' }}>{record.customer_order.customer.company}</div>
          )}
        </div>
      );
    } },
    { title: 'Ordine Fornitore', key: 'supplier_order', render: (record: SupplierQuote) => (
      record.supplier_order ? <Tag color="green">{record.supplier_order.order_number}</Tag> : '-'
    ) },
    { title: 'Importo Totale', dataIndex: 'total_amount', key: 'total_amount', render: (amount: number) => amount ? `€ ${formatCurrency(amount)}` : '-' },
    { title: 'Azioni', key: 'actions', render: (_: any, record: SupplierQuote) => (
      <Dropdown menu={{ items: getActionItems(record) }} trigger={['click']}>
        <Button icon={<MoreOutlined />} />
      </Dropdown>
    ) },
  ];

  const editingLocked = !!editingQuote && areQuoteRowsLocked(editingQuote.quote_status_id);

  return (
    <div>
      <Title level={2}>Gestione Preventivi Fornitori</Title>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Space wrap>
            <Input
              placeholder="Filtra per Numero Preventivo"
              style={{ width: 200 }}
              value={filters.quote_number}
              onChange={(e) => setFilters({ ...filters, quote_number: e.target.value || undefined })}
              allowClear
            />
            <Select
              placeholder="Filtra per Fornitore"
              style={{ width: 200 }}
              value={filters.supplier_id}
              onChange={(value) => setFilters({ ...filters, supplier_id: value })}
              showSearch
              optionFilterProp="children"
              allowClear
            >
              {suppliers.map((supplier) => (
                <Option key={supplier.id} value={supplier.id}>{supplier.name}</Option>
              ))}
            </Select>
            <Select
              placeholder="Filtra per Stato"
              style={{ width: 150 }}
              value={filters.status_id}
              onChange={(value) => setFilters({ ...filters, status_id: value })}
              allowClear
            >
              {quoteStatuses.map((status) => (
                <Option key={status.id} value={status.id}>{status.display_name}</Option>
              ))}
            </Select>
            <Select
              placeholder="Filtra per Ordine Cliente"
              style={{ width: 220 }}
              value={filters.customer_order_id}
              onChange={(value) => setFilters({ ...filters, customer_order_id: value })}
              showSearch
              optionFilterProp="children"
              allowClear
            >
              {customerOrders.map((order) => (
                <Option key={order.id} value={order.id}>{order.order_number}{order.customer?.company ? ` - ${order.customer.company}` : ''}</Option>
              ))}
            </Select>
            <Select
              placeholder="Filtra per Prodotto"
              style={{ width: 280 }}
              value={filters.product_id}
              onChange={(value) => setFilters({ ...filters, product_id: value })}
              showSearch
              optionFilterProp="children"
              allowClear
            >
              {products.map((product) => (
                <Option key={product.id} value={product.id}>
                  {generateProductDisplayName(product.internal_product_code, product.size, product.color, product.product_description)}
                </Option>
              ))}
            </Select>
            <RangePicker
              format="DD/MM/YYYY"
              placeholder={['Data Da', 'Data A']}
              onChange={handleDateRangeChange}
              value={[filters.date_from ? dayjs(filters.date_from) : null, filters.date_to ? dayjs(filters.date_to) : null]}
            />
            <Checkbox checked={filters.show_closed} onChange={(e) => setFilters({ ...filters, show_closed: e.target.checked })}>
              Visualizza preventivi chiusi
            </Checkbox>
            {canEdit && (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                Nuovo Preventivo
              </Button>
            )}
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={quotes}
          rowKey="id"
          loading={loading}
          pagination={{ total: quotes.length, pageSize: 10, showSizeChanger: true, showQuickJumper: true }}
          scroll={{ x: true }}
        />

        <Modal
          title={editingQuote ? (editingLocked ? 'Modifica Preventivo Fornitore (solo testata)' : 'Modifica Preventivo Fornitore') : 'Nuovo Preventivo Fornitore'}
          open={isFormModalVisible}
          onCancel={() => setIsFormModalVisible(false)}
          footer={null}
          width="90%"
          destroyOnClose
        >
          <SupplierQuoteForm
            initialValues={editingQuote}
            headerOnly={editingLocked}
            suppliers={suppliers}
            quoteStatuses={quoteStatuses}
            onSubmit={handleSubmit}
            onCancel={() => setIsFormModalVisible(false)}
          />
        </Modal>

        <Modal
          title="Cambia Stato Preventivo Fornitore"
          open={isStatusModalVisible}
          onCancel={() => setIsStatusModalVisible(false)}
          footer={null}
          destroyOnClose
        >
          <QuoteStatusModal
            quote={statusChangingQuote}
            quoteType="supplier"
            quoteStatuses={quoteStatuses}
            onSubmit={handleStatusUpdate}
            onCancel={() => setIsStatusModalVisible(false)}
          />
        </Modal>

        {viewingQuote && (
          <SupplierQuoteDetailsModal quote={viewingQuote} onClose={() => setViewingQuote(null)} />
        )}

        <ConvertSupplierQuoteModal
          visible={isConvertModalVisible}
          quote={convertingQuote}
          onClose={() => { setIsConvertModalVisible(false); setConvertingQuote(null); }}
          onConverted={loadQuotes}
        />
      </Card>
    </div>
  );
};

export default SupplierQuotes;
